import React from "react";
import { Row, Col, Form, Button } from "react-bootstrap";

/**
 * Band editor for pay type 4 (Banded Student Rate).
 *
 * The tutor is paid a set amount per lesson that depends on how many students were in
 * that lesson. Bands are an ordered list of amounts: the first is the amount for a lesson
 * with 1 student, the second for 2 students, and so on. The last band is open-ended, so a
 * lesson with more students than there are bands still pays the highest band.
 *
 * Amounts are shown without a currency symbol -- centres are not all in the same country.
 *
 * Shared by AddTutor and EditTutor. These are rates that get paid out, so they live in one
 * place rather than in two copies that can drift apart.
 */

const MIN_BANDS = 1;
const MAX_BANDS = 20;

export const DEFAULT_PAY_BANDS = ["0", "0", "0", "0", "0", "0"];

export const bandLabel = (index, total) => {
  const students = index + 1;
  if (index === total - 1) {
    return `${students} Student${students === 1 ? "" : "s"} or more`;
  }
  return `${students} Student${students === 1 ? "" : "s"}`;
};

/**
 * The config version in force on a given date, or null.
 *
 * Mirrors TimesheetCalculationEngine / TutorPayConfigRepository in the backend, which is
 * what actually prices a timesheet. This copy exists so the Add/Edit Timesheet modals can
 * preview a total that agrees with what saving will store, including when the period being
 * keyed in straddles a rate change.
 */
const findVersion = (versions, sessionDate) => {
  if (!Array.isArray(versions) || !sessionDate) return null;

  const date = new Date(sessionDate);
  let match = null;

  versions.forEach((version) => {
    const from = new Date(version.tpc_effective_from);
    if (date < from) return;

    // tpc_effective_to null means the version is still in force.
    if (version.tpc_effective_to && date > new Date(version.tpc_effective_to)) return;

    if (!match || from > new Date(match.tpc_effective_from)) match = version;
  });

  return match;
};

/**
 * The band that prices a lesson, or null.
 *
 * Largest tpb_student_from not exceeding the headcount, which gives "N or more" for free.
 * A lesson with no students matches no band and is unpaid.
 */
export const findBand = (versions, sessionDate, students) => {
  const count = Number(students) || 0;
  if (count < 1) return null;

  const version = findVersion(versions, sessionDate);
  if (!version || !version.bands || !version.bands.length) return null;

  let match = null;
  version.bands.forEach((band) => {
    const from = Number(band.tpb_student_from);
    if (from <= count && (!match || from > Number(match.tpb_student_from))) match = band;
  });

  return match;
};

export const bandDisplayLabel = (band) =>
  band ? `${band.tpb_student_from}${band.tpb_is_open_ended ? "+" : ""}` : "-";

/**
 * What a banded tutor earns across a set of lessons: each lesson pays the band matching
 * the number of students in it.
 */
export const calculateBandedTotal = (versions, sessions = []) =>
  sessions.reduce((total, session) => {
    const band = findBand(versions, session.session_date, session.no_of_student ?? session.no_of_students);
    return total + (band ? Number(band.tpb_amount) : 0);
  }, 0);

const TutorPayBands = ({
  bands,
  onBandsChange,
  effectiveFrom,
  onEffectiveFromChange,
  lastPaidDate,
  error,
  effectiveFromError,
}) => {
  const list = Array.isArray(bands) && bands.length ? bands : DEFAULT_PAY_BANDS;

  const setAmount = (index, amount) => {
    const next = [...list];
    next[index] = amount;
    onBandsChange(next);
  };

  const addBand = () => {
    if (list.length >= MAX_BANDS) return;
    onBandsChange([...list, "0"]);
  };

  const removeBand = () => {
    if (list.length <= MIN_BANDS) return;
    onBandsChange(list.slice(0, -1));
  };

  // Rates are effective-dated, so an already-paid period keeps calculating at the rates
  // that were in force when it was paid. Backdating past a paid timesheet does not rewrite
  // that timesheet, but it does mean the configuration no longer matches what was paid.
  const isBackdatedIntoPaidPeriod =
    effectiveFrom && lastPaidDate && new Date(effectiveFrom) <= new Date(lastPaidDate);

  return (
    <>
      <Row>
        <Col lg="4" md="6" sm="12" className="position-relative">
          <h6 className="form-lable required-field">Rates Effective From</h6>
          <Form.Control
            type="date"
            id="pay_effective_from"
            value={effectiveFrom || ""}
            onChange={(e) => onEffectiveFromChange(e.target.value)}
            className={effectiveFromError ? "input-error" : ""}
          />
          {effectiveFromError && <p className="error">{effectiveFromError}</p>}
        </Col>
      </Row>

      {isBackdatedIntoPaidPeriod && (
        <Row className="mt-2">
          <Col sm="12">
            <div className="alert alert-warning mb-0" role="alert">
              <strong>This date falls in a period that has already been paid.</strong>
              <div>
                The last paid timesheet for this tutor covers up to {lastPaidDate}. Timesheets
                that have already been generated will keep the rates they were calculated with,
                so changing the rates from this date will leave your pay configuration showing
                different amounts from the ones the tutor was actually paid. Mygooroo accepts no
                responsibility for accounting discrepancies that result from backdating rates
                into a period you have already paid out.
              </div>
            </div>
          </Col>
        </Row>
      )}

      <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
        <h6 className="mb-0">Rate Per Lesson, By Number Of Students</h6>
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="mr-2"
            onClick={removeBand}
            disabled={list.length <= MIN_BANDS}
          >
            Remove Band
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={addBand}
            disabled={list.length >= MAX_BANDS}
          >
            Add Band
          </Button>
        </div>
      </div>

      <Row>
        {list.map((amount, index) => (
          <Col lg="4" md="6" sm="12" className="position-relative mb-3" key={index}>
            <h6 className="form-lable required-field">{bandLabel(index, list.length)}</h6>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(index, e.target.value)}
            />
          </Col>
        ))}
      </Row>

      {error && <p className="error">{error}</p>}
    </>
  );
};

export default TutorPayBands;
